import type { WeeklyApprovalRow } from '@/services/googleAdsWeeklyService';
import type { SearchTermNegativeRecommendation } from '@/services/googleAdsWeeklyService';
import {
  convertingIds,
  isConvertingRec,
  recConversions,
} from './convertingSearchTerm';
import type {
  AdGroupRow,
  AdMonitorResponse,
  ConversionViewResponse,
  KeywordRow,
  MonitoredAd,
  StrategyGoogleRec,
  StructureResponse,
} from '@/services/googleAdsManagementService';

/** Pause Yes is this window, not a lifetime quality score. */
export const PAUSE_WINDOW_DISCLAIMER =
  'This window looks bad. That does not mean it is permanently bad — some keywords and ad groups recover next month. Re-enable later if it should run again.';

export const WEEKLY_FLOW_LABEL: Record<string, string> = {
  p2_negative_keywords: 'Exclude search terms',
  p2_ad_pauses: 'Pause ads',
  p2_keyword_pauses: 'Pause keywords',
};

export function countWeeklyItems(row: WeeklyApprovalRow): number {
  const payload = row.payload as {
    candidates?: unknown[];
    keywords?: unknown[];
    groups?: { ads?: unknown[] }[];
  };
  if (Array.isArray(payload.candidates)) return payload.candidates.length;
  if (Array.isArray(payload.keywords)) return payload.keywords.length;
  if (Array.isArray(payload.groups)) {
    return payload.groups.reduce((n, group) => n + (group.ads?.length ?? 0), 0);
  }
  return 0;
}

/** Same as backend `ad_monitor_service._NOT_SERVING`. ENABLED + DISAPPROVED
 *  serves nothing. APPROVED_LIMITED stays live — it still serves, just
 *  restricted (`_LIMITED` on the backend). */
const NOT_SERVING = new Set(['DISAPPROVED']);

export function isServingAd(ad: MonitoredAd): boolean {
  return (
    ad.status === 'ENABLED' &&
    !NOT_SERVING.has((ad.approval_status || '').toUpperCase())
  );
}

/**
 * Ads that still serve in this group.
 *
 * The monitor GAQL is `FROM ad_group_ad WHERE segments.date BETWEEN …`, so a
 * live ad with no impressions in the window is not in the list at all. That
 * undercounts group size and pushes rows toward the deep dive. Safe, keep it.
 */
export function servingAdsInGroup(
  ads: MonitoredAd[],
  adGroupId: string,
): MonitoredAd[] {
  return ads.filter((ad) => ad.ad_group_id === adGroupId && isServingAd(ad));
}

/** Easy pause needs a 30-day zero that is not one click over $25. */
export const EASY_WASTE_MIN_COST = 75;
export const EASY_WASTE_MIN_CLICKS = 5;
/** Ad-pause Yes also needs spend of at least this many times account CPA. */
export const EASY_AD_CPA_MULTIPLE = 2;

const THEME_STOP = new Set([
  'a',
  'and',
  'area',
  'central',
  'city',
  'company',
  'companies',
  'contractor',
  'contractors',
  'core',
  'counties',
  'county',
  'east',
  'for',
  'general',
  'home',
  'house',
  'in',
  'location',
  'locations',
  'me',
  'metro',
  'near',
  'north',
  'of',
  'or',
  'residential',
  'searches',
  'service',
  'services',
  'south',
  'specialist',
  'the',
  'top',
  'west',
]);

function meaningfulTokens(value: string): string[] {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(/\s+/)
    .map((token) => token.replace(/(ing|ers|er|s)$/g, ''))
    .filter((token) => token.length > 2 && !THEME_STOP.has(token));
}

/** Adjacent tokens as one word so `poly jacking` matches `Polyjacking`. */
function tokenSetWithCompounds(tokens: string[]): Set<string> {
  const out = new Set(tokens);
  for (let i = 0; i < tokens.length - 1; i += 1) {
    out.add(`${tokens[i]}${tokens[i + 1]}`);
  }
  return out;
}

export function hasEasyWasteTraffic(row: {
  cost?: number | null;
  clicks?: number | null;
}): boolean {
  return (
    (row.cost ?? 0) >= EASY_WASTE_MIN_COST ||
    (row.clicks ?? 0) >= EASY_WASTE_MIN_CLICKS
  );
}

/** Group theme after stripping geo/company filler. */
export function adGroupThemeKey(name: string): string {
  return meaningfulTokens(name).sort().join(' ');
}

/** The keyword is the group's own theme, not a leftover qualifier. */
export function isHeadKeyword(row: KeywordRow): boolean {
  const keywordTokens = meaningfulTokens(row.text);
  const groupTokens = meaningfulTokens(row.ad_group_name);
  if (keywordTokens.length === 0 || groupTokens.length === 0) return false;
  const keywordSet = tokenSetWithCompounds(keywordTokens);
  const groupSet = tokenSetWithCompounds(groupTokens);
  const overlap = [...groupSet].filter((token) => keywordSet.has(token));
  const needed = Math.max(
    1,
    Math.ceil(Math.min(keywordTokens.length, groupTokens.length) / 2),
  );
  return overlap.length >= needed;
}

/** Primary / bidding conversions only. `all_conversions` includes GBP
 *  visits, phone clicks, and other observation actions — it must not
 *  enter Easy CPA or the LP-conversion gate. */
export function adConversions(ad: {
  conversions?: number | null;
  all_conversions?: number | null;
}): number {
  return ad.conversions ?? 0;
}

/** Window CPA from structure groups when present, else from the ad list. */
export function accountWindowCpa(args: {
  ads?: MonitoredAd[];
  adGroups?: AdGroupRow[];
}): number | null {
  const rows = args.adGroups?.length ? args.adGroups : args.ads;
  if (!rows?.length) return null;
  const cost = rows.reduce((sum, row) => sum + (row.cost ?? 0), 0);
  const conversions = rows.reduce((sum, row) => sum + adConversions(row), 0);
  if (cost <= 0 || conversions <= 0) return null;
  return cost / conversions;
}

export function easyAdMinSpend(args: {
  ads?: MonitoredAd[];
  adGroups?: AdGroupRow[];
}): number | null {
  const cpa = accountWindowCpa(args);
  if (cpa == null) return null;
  return Math.max(EASY_WASTE_MIN_COST, cpa * EASY_AD_CPA_MULTIPLE);
}

export function landingPathKey(url: string | null | undefined): string {
  const raw = (url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return `${parsed.host}${path}`.toLowerCase();
  } catch {
    return raw.split('?')[0].replace(/\/+$/, '').toLowerCase();
  }
}

export function convertingLandingPaths(
  ads: MonitoredAd[] | undefined,
): Set<string> {
  const out = new Set<string>();
  for (const ad of ads || []) {
    if (adConversions(ad) <= 0) continue;
    const key = landingPathKey(ad.final_url);
    if (key) out.add(key);
  }
  return out;
}

export function lookWasteAdReason(
  ad: MonitoredAd,
  ads: MonitoredAd[] | undefined,
  adGroups?: AdGroupRow[],
): string {
  const floor = easyAdMinSpend({ ads, adGroups });
  const path = landingPathKey(ad.final_url);
  const converting = convertingLandingPaths(ads);
  if (floor == null) {
    return `${ad.campaign_name} / ${ad.ad_group_name} spent and converted nothing, but this window has no account conversions so CPA is unknown. Another ad stays live.`;
  }
  if (!path || !converting.has(path)) {
    return `${ad.campaign_name} / ${ad.ad_group_name} spent and converted nothing, but no ad on this landing page converted in this window. Zero-conv may be measurement.`;
  }
  if ((ad.cost ?? 0) < floor) {
    return `${ad.campaign_name} / ${ad.ad_group_name} spent $${Math.round(ad.cost ?? 0)} with no conversions, but Easy needs about 2× account CPA ($${Math.round(floor)}).`;
  }
  return `${ad.campaign_name} / ${ad.ad_group_name} spent and converted nothing, but this window is too thin for a one-click pause. Another ad stays live.`;
}

/** Easy Yes: sibling-safe waste ad with proven LP measurement and 2× CPA spend. */
export function easyWasteAds(
  ads: MonitoredAd[] | undefined,
  adGroups?: AdGroupRow[],
): MonitoredAd[] {
  if (!ads) return [];
  const floor = easyAdMinSpend({ ads, adGroups });
  if (floor == null) return [];
  const converting = convertingLandingPaths(ads);
  return ads.filter((ad) => {
    if (!ad.is_waste || !isServingAd(ad) || !ad.resource_name) {
      return false;
    }
    if (!hasEasyWasteTraffic(ad) || (ad.cost ?? 0) < floor) return false;
    const path = landingPathKey(ad.final_url);
    if (!path || !converting.has(path)) return false;
    return servingAdsInGroup(ads, ad.ad_group_id).length > 1;
  });
}

/** Sibling-safe waste ads that failed the Easy ad-pause gates. */
export function lookWasteAds(
  ads: MonitoredAd[] | undefined,
  adGroups?: AdGroupRow[],
  readsOpen = true,
): MonitoredAd[] {
  if (!ads) return [];
  const easyIds = new Set(
    readsOpen
      ? easyWasteAds(ads, adGroups).map((ad) => ad.resource_name || ad.ad_id)
      : [],
  );
  return ads.filter((ad) => {
    if (!ad.is_waste || !isServingAd(ad)) return false;
    if (servingAdsInGroup(ads, ad.ad_group_id).length <= 1) return false;
    return !easyIds.has(ad.resource_name || ad.ad_id);
  });
}

export function lastAdWaste(ads: MonitoredAd[] | undefined): MonitoredAd[] {
  if (!ads) return [];
  return ads.filter((ad) => {
    if (!ad.is_waste || !isServingAd(ad)) return false;
    return servingAdsInGroup(ads, ad.ad_group_id).length <= 1;
  });
}

function keywordGroupKey(row: KeywordRow): string {
  return `${row.campaign_name}::${row.ad_group_name}`;
}

/** Sibling that can justify a waste-keyword Yes: live and not itself waste. */
function supportingKeywordsInGroup(
  keywords: KeywordRow[],
  row: KeywordRow,
): KeywordRow[] {
  const key = keywordGroupKey(row);
  return keywords.filter(
    (item) =>
      item.status === 'ENABLED' &&
      !item.is_waste &&
      keywordGroupKey(item) === key,
  );
}

/** Easy Yes: waste keyword with a non-waste keyword still live in the group. */
export function easyWasteKeywords(
  keywords: KeywordRow[] | undefined,
): KeywordRow[] {
  if (!keywords) return [];
  return keywords.filter((row) => {
    if (!row.is_waste || row.status !== 'ENABLED' || !row.resource_name) {
      return false;
    }
    if (!hasEasyWasteTraffic(row) || isHeadKeyword(row)) return false;
    return supportingKeywordsInGroup(keywords, row).length > 0;
  });
}

/** Sibling-safe waste keywords that are the group theme or a thin window. */
export function lookWasteKeywords(
  keywords: KeywordRow[] | undefined,
): KeywordRow[] {
  if (!keywords) return [];
  return keywords.filter((row) => {
    if (!row.is_waste || row.status !== 'ENABLED') return false;
    if (supportingKeywordsInGroup(keywords, row).length === 0) return false;
    return !hasEasyWasteTraffic(row) || isHeadKeyword(row);
  });
}

function searchTermGroupKey(
  campaign?: string | null,
  adGroup?: string | null,
): string {
  return `${(campaign || '').trim().toLowerCase()}::${(adGroup || '').trim().toLowerCase()}`;
}

/**
 * Open search-term excludes for this keyword's group (and this keyword when
 * Google named the trigger). Keyword pause waits until those are gone.
 */
export function keywordHasOpenSearchTerms(
  row: KeywordRow,
  recs: SearchTermNegativeRecommendation[] | undefined,
): boolean {
  if (!recs?.length) return false;
  const key = searchTermGroupKey(row.campaign_name, row.ad_group_name);
  const text = row.text.trim().toLowerCase();
  return recs.some((rec) => {
    if (rec.already_applied) return false;
    const metrics = rec.metrics;
    if (searchTermGroupKey(metrics.campaign_name, metrics.ad_group_name) !== key) {
      return false;
    }
    const trigger = (metrics.triggering_keyword || '').trim().toLowerCase();
    if (!trigger) return true;
    return trigger === text;
  });
}

/** Sibling-safe waste keywords with no open search-term recs in the way. */
export function easyWasteKeywordsReady(
  keywords: KeywordRow[] | undefined,
  recs: SearchTermNegativeRecommendation[] | undefined,
): KeywordRow[] {
  return easyWasteKeywords(keywords).filter(
    (row) => !keywordHasOpenSearchTerms(row, recs),
  );
}

/** Sibling-safe waste keywords that still have junk search terms to exclude. */
export function keywordsWaitingOnSearchTerms(
  keywords: KeywordRow[] | undefined,
  recs: SearchTermNegativeRecommendation[] | undefined,
): KeywordRow[] {
  return easyWasteKeywords(keywords).filter((row) =>
    keywordHasOpenSearchTerms(row, recs),
  );
}

export function lastKeywordWaste(
  keywords: KeywordRow[] | undefined,
): KeywordRow[] {
  if (!keywords) return [];
  return keywords.filter((row) => {
    if (!row.is_waste || row.status !== 'ENABLED') return false;
    return supportingKeywordsInGroup(keywords, row).length === 0;
  });
}

/**
 * Healthy sibling for an ad-group Yes: ENABLED, not waste (waste needs $25),
 * served in the window, and not a dark group (every enabled ad disapproved).
 */
export function isHealthyAdGroup(
  group: AdGroupRow,
  darkAdGroupNames: Iterable<string> = [],
): boolean {
  if (group.status !== 'ENABLED' || group.is_waste) return false;
  if ((group.impressions ?? 0) <= 0 && (group.cost ?? 0) <= 0) return false;
  // Dark groups are matched by *name* (ad read ↔ structure read). Same name
  // in two campaigns over-excludes — safe direction. Do not "fix" this to an
  // id match unless both sides carry a group id.
  return !new Set(darkAdGroupNames).has(group.name);
}

function healthyGroupsInCampaign(
  groups: AdGroupRow[],
  campaignName: string,
  darkAdGroupNames: Iterable<string>,
): AdGroupRow[] {
  const dark = new Set(darkAdGroupNames);
  return groups.filter(
    (group) =>
      group.campaign_name === campaignName && isHealthyAdGroup(group, dark),
  );
}

function sameThemeHealthy(
  groups: AdGroupRow[],
  row: AdGroupRow,
  darkAdGroupNames: Iterable<string>,
): AdGroupRow[] {
  const theme = adGroupThemeKey(row.name);
  if (!theme) return [];
  return healthyGroupsInCampaign(groups, row.campaign_name, darkAdGroupNames).filter(
    (group) => adGroupThemeKey(group.name) === theme,
  );
}

/** Easy Yes: waste ad group only if a same-theme group still served. */
export function easyWasteAdGroups(
  groups: AdGroupRow[] | undefined,
  ctx: { darkAdGroupNames?: string[]; adsReadOk: boolean } = {
    adsReadOk: false,
  },
): AdGroupRow[] {
  if (!groups || !ctx.adsReadOk) return [];
  const dark = ctx.darkAdGroupNames ?? [];
  return groups.filter((group) => {
    if (!group.is_waste || group.status !== 'ENABLED' || !group.resource_name) {
      return false;
    }
    if (!hasEasyWasteTraffic(group)) return false;
    return sameThemeHealthy(groups, group, dark).length > 0;
  });
}

/** Waste theme with a healthy *other* theme in the campaign — evidence only, not Yes. */
export function themeWasteAdGroups(
  groups: AdGroupRow[] | undefined,
  ctx: { darkAdGroupNames?: string[]; adsReadOk?: boolean } = {},
): AdGroupRow[] {
  if (!groups) return [];
  const dark = ctx.darkAdGroupNames ?? [];
  return groups.filter((group) => {
    if (!group.is_waste || group.status !== 'ENABLED') return false;
    if (sameThemeHealthy(groups, group, dark).length > 0) return false;
    return healthyGroupsInCampaign(groups, group.campaign_name, dark).length > 0;
  });
}

export function lastAdGroupWaste(
  groups: AdGroupRow[] | undefined,
  ctx: { darkAdGroupNames?: string[] } = {},
): AdGroupRow[] {
  if (!groups) return [];
  const dark = ctx.darkAdGroupNames ?? [];
  return groups.filter((group) => {
    if (!group.is_waste || group.status !== 'ENABLED') return false;
    return healthyGroupsInCampaign(groups, group.campaign_name, dark).length === 0;
  });
}

function searchTermSpend(rec: SearchTermNegativeRecommendation): {
  cost: number;
  clicks: number;
} {
  return {
    cost: (rec.metrics?.cost_micros ?? 0) / 1_000_000,
    clicks: rec.metrics?.clicks ?? 0,
  };
}

const COMMERCIAL_WHO =
  /\bwho\b.+\b(installs?|can|to call|does|do i call|works)\b/;
const COMMERCIAL_PRICE = /\bhow much\b|\bhow expensive\b/;
const COMMERCIAL_BEST =
  /\bbest\b.+\b(near me|companies|company|contractors?)\b/;
const RESEARCH_BEST = /\b(reviews?|complaints|sealer|tools?|method)\b/;
const DIY_HOW_TO =
  /\bhow (do i|to) (make|build|install|do)\b|\byoutube\b|\bguide\b/;

export function isCommercialBuyerTerm(term: string): boolean {
  const phrase = term.trim().toLowerCase();
  if (COMMERCIAL_WHO.test(phrase) || COMMERCIAL_PRICE.test(phrase)) return true;
  return COMMERCIAL_BEST.test(phrase) && !RESEARCH_BEST.test(phrase);
}

/** The search term's conversions over every campaign and ad group. */
export function searchTermConversions(
  rec: SearchTermNegativeRecommendation,
): number {
  return recConversions(rec);
}

export function isEasySearchTerm(
  rec: SearchTermNegativeRecommendation,
): boolean {
  // One word of a longer own-brand name ("edelman" of Edelman-Lyon): may be
  // the client, so a person reads it (Look), never Easy.
  if (rec.brand_collision) return false;
  if (rec.already_applied) return false;
  // Converting anywhere (another campaign, or a fraction) is never Easy.
  if (isConvertingRec(rec)) return false;
  if (isCommercialBuyerTerm(rec.search_term)) return false;
  const spend = searchTermSpend(rec);
  if (spend.cost <= 0 && spend.clicks <= 0) return false;
  const category = rec.category;
  const term = rec.search_term.toLowerCase();
  if (
    category === 'competitor_or_other_brand' ||
    category === 'competitor'
  ) {
    return true;
  }
  if (category === 'diy_or_self_help' || category === 'diy') return true;
  if (category === 'job_seeker') return true;
  // Campaign-exclude and single-token cities stay Look. Easy geo is a
  // known home area, the full query as PHRASE, and a clear out-of-footprint
  // place (Colorado Springs). Absent out_of_footprint on old payloads → Look.
  if (category === 'geo_mismatch') {
    const negative = rec.negative_keyword.trim().toLowerCase();
    return (
      rec.home_area_known === true &&
      rec.out_of_footprint === true &&
      rec.search_term.trim().toLowerCase() === negative
    );
  }
  if (
    category === 'low_intent' &&
    /\breviews?\b|\bcomplaints\b/.test(term)
  ) {
    return true;
  }
  if (category === 'informational' && DIY_HOW_TO.test(term)) return true;
  return false;
}

export function strategyEasyReadsOpen(args: {
  adsFailed?: boolean;
  structureFailed?: boolean;
  searchTermsFailed?: boolean;
}): boolean {
  return !args.adsFailed && !args.structureFailed && !args.searchTermsFailed;
}

export function easySearchTermRecs(
  recs: SearchTermNegativeRecommendation[] | undefined,
  readsOpen = true,
): SearchTermNegativeRecommendation[] {
  if (!readsOpen || !recs) return [];
  // A term converting on ANY of its recommendations is converting on all.
  const converting = convertingIds(recs);
  return recs.filter((rec) => !converting.has(rec.id) && isEasySearchTerm(rec));
}

/** Classified but not a one-click Yes — confirm on Search Terms. */
export function lookSearchTermRecs(
  recs: SearchTermNegativeRecommendation[] | undefined,
  readsOpen = true,
): SearchTermNegativeRecommendation[] {
  if (!recs) return [];
  const converting = convertingIds(recs);
  return recs.filter((rec) => {
    if (rec.already_applied) return false;
    if (!readsOpen) return true;
    return converting.has(rec.id) || !isEasySearchTerm(rec);
  });
}

export interface DeepDiveItem {
  id: string;
  title: string;
  why: string;
  target: string;
}

/** Agency-normal hygiene. Not a finding and not a Yes. */
export function buildLookDisclaimers(args: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
}): DeepDiveItem[] {
  const items: DeepDiveItem[] = [];
  const ads = args.ads;
  const conversions = args.conversions;

  if (conversions?.read_ok && (conversions.gbp_primary_count ?? 0) > 0) {
    items.push({
      id: 'gbp-primary',
      title: 'Conversion hygiene',
      why: `${conversions.gbp_primary_count} Google Business Profile action(s) stay primary because Google-hosted actions cannot be set to secondary. Core does not count them as bidding conversions. This will not affect performance.`,
      target: 'conversion-actions',
    });
  }
  if (conversions?.read_ok && (conversions.silent_count ?? 0) > 0) {
    items.push({
      id: 'silent-primary',
      title: 'Silent primary conversions',
      why: `${conversions.silent_count} primary action(s) recorded nothing in this window. Unused Google-hosted primaries are common on accounts that came from another agency. This will not affect performance.`,
      target: 'conversion-actions',
    });
  }
  if (ads?.read_ok && (ads.dark_ad_groups?.length ?? 0) > 0) {
    items.push({
      id: 'dark-ad-groups',
      title: 'Ad groups with every enabled ad disapproved',
      why: `${ads.dark_ad_groups.length} ad group(s) have every enabled ad disapproved: ${ads.dark_ad_groups.slice(0, 4).join(', ')}${ads.dark_ad_groups.length > 4 ? '…' : ''}. Usually a leftover landing page from a prior agency (destination mismatch). Do not rewrite or delete to clear this. This will not affect performance.`,
      target: 'ad-health',
    });
  }
  if (ads?.read_ok && (ads.disapproved_count ?? 0) > 0) {
    items.push({
      id: 'disapproved',
      title: 'Disapproved ads',
      why: `${ads.disapproved_count} ad(s) failed policy, usually a landing page that is no longer live. Updating the URL or deleting the ad is not required. This will not affect performance.`,
      target: 'ad-health',
    });
  }
  return items;
}

export function buildGoogleRecLookItems(
  recs: StrategyGoogleRec[] | undefined,
): DeepDiveItem[] {
  if (!recs?.length) return [];
  return recs.map((rec) => ({
    id: rec.resource_name,
    title: rec.title,
    why: rec.why,
    target: 'google-recommendations',
  }));
}

export function buildSearchTermFirstLookItems(
  keywords: KeywordRow[] | undefined,
  recs: SearchTermNegativeRecommendation[] | undefined,
): DeepDiveItem[] {
  return keywordsWaitingOnSearchTerms(keywords, recs).map((row) => ({
    id: `st-first-${row.resource_name || row.criterion_id}`,
    title: `Search-term refinement first · ${row.text}`,
    why: `${row.campaign_name} / ${row.ad_group_name} looks wasteful in this window, but Core still has search-term excludes for this ad group. Refine those first. Pause only if remaining triggered terms are relevant and still not converting.`,
    target: 'keywords',
  }));
}

export function lookListCount(args: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  structure?: StructureResponse;
  googleRecs?: StrategyGoogleRec[];
  searchTermRecs?: SearchTermNegativeRecommendation[];
  easyReadsOpen?: boolean;
}): number {
  const readsOpen = args.easyReadsOpen !== false;
  return (
    buildDeepDiveItems({ ...args, easyReadsOpen: readsOpen }).length +
    buildLookDisclaimers(args).length +
    buildGoogleRecLookItems(args.googleRecs).length +
    buildSearchTermFirstLookItems(
      args.structure?.read_ok ? args.structure.keywords : undefined,
      args.searchTermRecs,
    ).length +
    buildLookSearchTermItems(args.searchTermRecs, readsOpen).length
  );
}

export function buildLookSearchTermItems(
  recs: SearchTermNegativeRecommendation[] | undefined,
  readsOpen = true,
): DeepDiveItem[] {
  return lookSearchTermRecs(recs, readsOpen).map((rec) => ({
    id: `st-look-${rec.id}`,
    title: `Confirm exclude · ${rec.search_term}`,
    why: `${rec.negative_keyword} (${rec.match_type}). ${rec.reason || rec.evidence || 'Not a one-click Yes — confirm this is junk, not a buyer query.'}`.trim(),
    target: 'keywords',
  }));
}

export function buildDeepDiveItems(args: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  structure?: StructureResponse;
  easyReadsOpen?: boolean;
}): DeepDiveItem[] {
  const items: DeepDiveItem[] = [];
  const ads = args.ads;
  const structure = args.structure;
  const readsOpen = args.easyReadsOpen !== false;

  for (const ad of lastAdWaste(ads?.read_ok ? ads.ads : undefined)) {
    items.push({
      id: `last-ad-${ad.resource_name || ad.ad_id}`,
      title: `Only live ad in ${ad.ad_group_name}`,
      why: `${ad.label || ad.headline || ad.ad_id} spent and converted nothing, but pausing it empties the ad group. Pause or restructure — do not Yes from here.`,
      target: 'ad-health',
    });
  }

  for (const ad of lookWasteAds(
    ads?.read_ok ? ads.ads : undefined,
    structure?.read_ok ? structure.ad_groups : undefined,
    readsOpen,
  )) {
    items.push({
      id: `thin-ad-${ad.resource_name || ad.ad_id}`,
      title: `Confirm ad · ${ad.label || ad.headline || ad.ad_id}`,
      why: lookWasteAdReason(
        ad,
        ads?.read_ok ? ads.ads : undefined,
        structure?.read_ok ? structure.ad_groups : undefined,
      ),
      target: 'ad-health',
    });
  }

  for (const row of lookWasteKeywords(
    structure?.read_ok ? structure.keywords : undefined,
  )) {
    items.push({
      id: `look-kw-${row.resource_name || row.criterion_id}`,
      title: `Confirm keyword · ${row.text}`,
      why: isHeadKeyword(row)
        ? `${row.campaign_name} / ${row.ad_group_name} looks wasteful, but this is the group's theme. A Yes would pause the service query.`
        : `${row.campaign_name} / ${row.ad_group_name} spent and converted nothing, but this window is too thin for a one-click pause.`,
      target: 'keywords',
    });
  }

  for (const group of themeWasteAdGroups(
    structure?.read_ok ? structure.ad_groups : undefined,
    { darkAdGroupNames: ads?.read_ok ? ads.dark_ad_groups : undefined },
  )) {
    items.push({
      id: `theme-${group.resource_name || group.ad_group_id}`,
      title: `Pause or keep ${group.name}`,
      why: `${group.campaign_name} spent and converted nothing. Another group in this campaign is healthy, but it is a different theme. A Yes would drop this service line.`,
      target: 'ad-groups',
    });
  }

  for (const row of lastKeywordWaste(
    structure?.read_ok ? structure.keywords : undefined,
  )) {
    items.push({
      id: `last-kw-${row.resource_name || row.criterion_id}`,
      title: `Only live keyword in ${row.ad_group_name}`,
      why: `${row.text} spent and converted nothing, but pausing it empties the ad group. Confirm the window — this is not a Yes from here.`,
      target: 'keywords',
    });
  }

  const lastGroups = lastAdGroupWaste(
    structure?.read_ok ? structure.ad_groups : undefined,
    { darkAdGroupNames: ads?.read_ok ? ads.dark_ad_groups : undefined },
  );
  const byCampaign = new Map<string, AdGroupRow[]>();
  for (const group of lastGroups) {
    const key = group.campaign_name || 'Campaign';
    const list = byCampaign.get(key) ?? [];
    list.push(group);
    byCampaign.set(key, list);
  }
  for (const [campaign, groups] of byCampaign) {
    items.push({
      id: `campaign-${campaign}`,
      title: `Pause or restructure ${campaign}`,
      why: `${groups.length} ad group(s) spent and converted nothing, and no healthy group stays. A Yes here would kill the campaign. Decide pause vs restructure, then where the budget goes.`,
      target: 'ad-groups',
    });
  }

  return items;
}

export interface ReadFailure {
  source: 'ads' | 'conversions' | 'structure';
  message: string;
}

const READ_FALLBACK: Record<ReadFailure['source'], string> = {
  ads: 'Could not read ads for this account',
  conversions: 'Could not read conversions for this account',
  structure: 'Could not read structure for this account',
};

/** Failed reads are incomplete, not empty. Absence is never "all clear".
 *
 * ⛔ VIKTOR (#2248 review): a `read_ok:false` payload is not the only way a
 * read can fail — the query itself can error before any payload comes back
 * at all (network error, 500), leaving `state` undefined. That case used to
 * fall through this function silently: no banner, no lookCount, and
 * StrategyDeepDive would print "nothing that needs a deep dive" over a read
 * that never happened. The `*Error` flags cover that gap — pass a query's
 * `isError` through for a source when it has no payload to check `read_ok`
 * on. When a payload DID come back, its own `read_ok` is authoritative and
 * the query-error flag for that source is ignored, so a source is never
 * double-counted. */
export function collectReadFailures(args: {
  ads?: AdMonitorResponse;
  conversions?: ConversionViewResponse;
  structure?: StructureResponse;
  adsError?: boolean;
  conversionsError?: boolean;
  structureError?: boolean;
}): ReadFailure[] {
  const errorFlags: Record<ReadFailure['source'], boolean | undefined> = {
    ads: args.adsError,
    conversions: args.conversionsError,
    structure: args.structureError,
  };
  const out: ReadFailure[] = [];
  for (const source of ['ads', 'conversions', 'structure'] as const) {
    const state = args[source];
    if (state && !state.read_ok) {
      out.push({
        source,
        message: state.message?.trim() || READ_FALLBACK[source],
      });
    } else if (!state && errorFlags[source]) {
      out.push({ source, message: READ_FALLBACK[source] });
    }
  }
  return out;
}
